import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { isLocale, isTranslatedLocale, type DecisionTranslationContent, type Locale, type MeResponse, type PackageTranslationContent, type SectionTranslationContent, type ShownTranslation, type TranslationContent } from '@platform/shared';
import { api } from '../../api/client';
import { useLocale } from '../../i18n';

/**
 * The language a person reads documents in (planning-app.md §3.7 Layer 3): `people.reading_locale`
 * when set, else the UI language; the pseudo-locale sweep reads English (there is no en-XA cache).
 */
export function useReadingLocale(me: MeResponse | null | undefined): Locale {
  const { locale } = useLocale();
  const ui: Locale = isLocale(locale) ? locale : 'en';
  return me?.person.readingLocale ?? ui;
}

export interface LookupKey {
  unitKey: string;
  sourceHash?: string;
}

const CHUNK = 200;

/** The lookup key for `['translations', locale, …]` queries, so a decision made on this device refreshes the cache at once. */
export const TRANSLATIONS_KEY = ['translations'] as const;

/**
 * The cache read for a page's units in one request (≤ 200 keys per call, chunked beyond). Off in
 * English: nothing is fetched and every unit renders its English. Keys are sorted into the query
 * key so the same set from two components shares one request.
 */
export function useTranslationLookup(locale: Locale, keys: LookupKey[]): { byKey: Map<string, ShownTranslation>; isPending: boolean } {
  const sorted = useMemo(() => [...keys].sort((a, b) => a.unitKey.localeCompare(b.unitKey)), [keys]);
  const serial = sorted.map((k) => `${k.unitKey}@${k.sourceHash ?? ''}`).join('\n');
  const enabled = isTranslatedLocale(locale) && sorted.length > 0;
  const q = useQuery({
    queryKey: [...TRANSLATIONS_KEY, locale, serial],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      if (!isTranslatedLocale(locale)) return [];
      const out: ShownTranslation[] = [];
      for (let i = 0; i < sorted.length; i += CHUNK) {
        const res = await api.translationsLookup({ locale, keys: sorted.slice(i, i + CHUNK) });
        out.push(...res.translations);
      }
      return out;
    },
  });
  const byKey = useMemo(() => new Map((q.data ?? []).map((t) => [t.unitKey, t])), [q.data]);
  return { byKey, isPending: enabled && q.isPending };
}

/* The content union, narrowed by the unit kind (zod validated the shape server-side). */
export const asDecision = (c: TranslationContent | null | undefined): DecisionTranslationContent | null => (c && 'title' in c ? c : null);
export const asPackage = (c: TranslationContent | null | undefined): PackageTranslationContent | null => (c && 'what' in c ? c : null);
export const asSection = (c: TranslationContent | null | undefined): SectionTranslationContent | null => (c && 'text' in c && !('title' in c) ? c : null);
